import api from './api';
import { ApiResponse, PageResponse } from '../types/api';
import {
  StockItemResponseDTO,
  StockFilters,
  StockMovementRequestDTO,
  StockMovementResponseDTO,
  StockStats,
  StockCreateRequestDTO,
} from '../types/stock';

// Bases configuráveis para adequar aos caminhos reais da API
// Ajuste via .env se necessário:
// REACT_APP_STOCK_ITEMS_URL, REACT_APP_STOCK_MOVEMENTS_URL, REACT_APP_STOCK_MOVEMENT_TYPES_URL
// Com base no padrão informado: "/api/v1/estoque"
const stockBaseUrl = process.env.REACT_APP_STOCK_ITEMS_URL ?? '/v1/estoque';
const movementsBaseUrl = process.env.REACT_APP_STOCK_MOVEMENTS_URL ?? '/v1/estoque/movimentos';
// Tipos de movimento: por padrão usamos lista estática.
// Se houver URL configurada via env (REACT_APP_STOCK_MOVEMENT_TYPES_URL), tentamos a API;
// caso contrário, retornamos os tipos padrões sem chamar backend.
const movementTypesUrl = process.env.REACT_APP_STOCK_MOVEMENT_TYPES_URL;

export const stockService = {
  // Normalização de itens de estoque para compatibilizar com variações de contrato
  normalizeItem(raw: any): StockItemResponseDTO {
    return {
      id: raw.id,
      produtoId: raw.idProduto ?? raw.produtoId ?? raw.productId,
      produtoNome: raw.nomeProduto ?? raw.produtoNome,
      produtoCodigo: raw.produtoCodigo ?? raw.codigoProduto ?? raw.codigo,
      localId: raw.idLocal ?? raw.localId ?? raw.depositoId ?? raw.almoxarifadoId,
      localNome: raw.nomeLocal ?? raw.localNome ?? raw.depositoNome ?? raw.almoxarifadoNome,
      quantidadeAtual: raw.quantidadeDisponivel ?? raw.saldo ?? raw.quantidadeAtual ?? raw.quantidade ?? raw.qtd ?? 0,
      reservado: raw.quantidadeReservada ?? raw.reservado ?? raw.qtdReservada ?? 0,
      minimo: raw.quantidadeMinima ?? raw.estoqueMinimo ?? raw.minimo,
      maximo: raw.quantidadeMaxima ?? raw.estoqueMaximo ?? raw.maximo,
      ativo: raw.ativo ?? raw.active,
      updatedAt: raw.dataAtualizacao ?? raw.updatedAt,
    };
  },

  // Normalização de movimentos de estoque
  normalizeMovement(raw: any): StockMovementResponseDTO {
    return {
      id: raw.id,
      produtoId: raw.produtoId ?? raw.productId,
      produtoNome: raw.produtoNome ?? raw.nomeProduto,
      tipo: raw.tipo ?? raw.tipoMovimentacao,
      quantidade: raw.quantidade ?? raw.qtd ?? raw.quantidadeAtual ?? 0,
      origemLocalId: raw.origemLocalId ?? raw.origemId ?? raw.depositoOrigemId,
      destinoLocalId: raw.destinoLocalId ?? raw.destinoId ?? raw.depositoDestinoId,
      origemLocalNome: raw.origemLocalNome ?? raw.nomeOrigem ?? raw.depositoOrigemNome,
      destinoLocalNome: raw.destinoLocalNome ?? raw.nomeDestino ?? raw.depositoDestinoNome,
      origemEmpresaNome: raw.origemEmpresaNome ?? raw.nomeOrigemEmpresa ?? raw.empresaOrigemNome,
      destinoEmpresaNome: raw.destinoEmpresaNome ?? raw.nomeDestinoEmpresa ?? raw.empresaDestinoNome,
      observacoes: raw.observacoes ?? raw.obs,
      // Datas: prioriza 'dataMovimentacao' quando disponível
      dataMovimento: raw.dataMovimentacao ?? raw.dataMovimento ?? raw.data,
      createdAt: raw.createdAt ?? raw.criadoEm,
    };
  },

  // Listar itens de estoque com paginação e filtros
  async list(
    params?: StockFilters & { page?: number; size?: number; sort?: string }
  ): Promise<ApiResponse<PageResponse<StockItemResponseDTO>>> {
    const searchParams = new URLSearchParams();

    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    if (params?.sort) searchParams.append('sort', params.sort);

    // Alinhar nomes de parâmetros com API v1 (Estoque)
    if (params?.produtoId) searchParams.append('idProduto', params.produtoId);
    if (params?.localId) searchParams.append('idLocal', params.localId);
    if (params?.abaixoMinimo !== undefined) searchParams.append('abaixoMinimo', String(params.abaixoMinimo));
    if (params?.zerado !== undefined) searchParams.append('zerado', String(params.zerado));
    if (params?.ativo !== undefined) searchParams.append('ativo', String(params.ativo));

    const queryString = searchParams.toString();
    const url = queryString ? `${stockBaseUrl}?${queryString}` : stockBaseUrl;
    const response = await api.get<ApiResponse<PageResponse<any>>>(url);
    const original = response.data;
    const mapped: PageResponse<StockItemResponseDTO> = {
      ...original.data,
      content: (original.data?.content ?? []).map((it: any) => this.normalizeItem(it)),
    };
    return { ...original, data: mapped } as ApiResponse<PageResponse<StockItemResponseDTO>>;
  },

  // Buscar item de estoque por produto e (opcionalmente) local
  async getByProduct(
    produtoId: string,
    localId?: string
  ): Promise<ApiResponse<PageResponse<StockItemResponseDTO>>> {
    // Conforme documentação: GET /v1/estoque/produto/{produtoId}
    // Opcionalmente aceitar filtro de local via query (localId)
    let url = `${stockBaseUrl}/produto/${encodeURIComponent(produtoId)}`;
    const params = new URLSearchParams();
    if (localId) {
      params.append('localId', localId);
    }
    const qs = params.toString();
    if (qs) url = `${url}?${qs}`;

    // A API retorna um único item; normalizamos para PageResponse com 1 conteúdo
    const response = await api.get<ApiResponse<any>>(url);
    const original = response.data;
    const rawItem = (original && 'data' in original) ? original.data : original; // tolerar formatos diferentes
    const content = rawItem ? [this.normalizeItem(rawItem)] : [];
    const mapped: PageResponse<StockItemResponseDTO> = {
      content,
      totalElements: content.length,
      totalPages: 1,
      size: content.length || 0,
      number: 0,
    } as PageResponse<StockItemResponseDTO>;
    return { ...original, data: mapped } as ApiResponse<PageResponse<StockItemResponseDTO>>;
  },

  // Estatísticas gerais de estoque
  async getStats(): Promise<ApiResponse<StockStats>> {
    const response = await api.get<ApiResponse<StockStats>>(`${stockBaseUrl}/estatisticas`);
    return response.data;
  },

  // Listar movimentos de estoque
  async listMovements(
    params?: { page?: number; size?: number; produtoId?: string; localId?: string; tipoMovimentacao?: string }
  ): Promise<ApiResponse<PageResponse<StockMovementResponseDTO>>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.append('page', params.page.toString());
    if (params?.size !== undefined) searchParams.append('size', params.size.toString());
    // Manter possíveis filtros adicionais
    if (params?.localId) searchParams.append('idLocal', params.localId);
    if (params?.tipoMovimentacao) searchParams.append('tipoMovimentacao', params.tipoMovimentacao);

    // Ajuste de rota: quando houver produtoId usar 
    // "/v1/estoque/movimentos/produto/<id>" com paginação via query
    let url = movementsBaseUrl;
    if (params?.produtoId) {
      url = `${movementsBaseUrl}/produto/${encodeURIComponent(params.produtoId)}`;
    }
    const queryString = searchParams.toString();
    if (queryString) url = `${url}?${queryString}`;
    const response = await api.get<ApiResponse<PageResponse<any>>>(url);
    const original = response.data;
    const mapped: PageResponse<StockMovementResponseDTO> = {
      ...original.data,
      content: (original.data?.content ?? []).map((mv: any) => this.normalizeMovement(mv)),
    };
    return { ...original, data: mapped } as ApiResponse<PageResponse<StockMovementResponseDTO>>;
  },

  // Criar movimento de estoque (entrada/saída/transferência/ajuste)
  async createMovement(
    data: StockMovementRequestDTO
  ): Promise<ApiResponse<StockMovementResponseDTO>> {
    const response = await api.post<ApiResponse<StockMovementResponseDTO>>(movementsBaseUrl, data);
    return response.data;
  },

  // Criar registro de estoque
  async createStock(
    data: StockCreateRequestDTO
  ): Promise<ApiResponse<StockItemResponseDTO>> {
    const response = await api.post<ApiResponse<any>>(stockBaseUrl, data);
    // Normaliza resposta para o modelo usado na listagem
    const normalized = this.normalizeItem(response.data?.data ?? response.data);
    return { success: true, message: 'OK', data: normalized } as ApiResponse<StockItemResponseDTO>;
  },

  // Tipos de movimento: usa API apenas se URL estiver configurada; senão, lista estática
  async getMovementTypes(): Promise<ApiResponse<string[]>> {
    const staticTypes = [
      'ENTRADA',
      'SAIDA',
      'DEVOLUCAO',
      'AJUSTE',
      'TRANSFERENCIA',
      'PERDA',
      'INVENTARIO',
    ];

    if (!movementTypesUrl) {
      return {
        success: true,
        message: 'Tipos de movimento (estático) carregados',
        data: staticTypes,
        timestamp: new Date().toISOString(),
      };
    }

    try {
      const response = await api.get<ApiResponse<any>>(movementTypesUrl);
      const raw = response.data?.data ?? response.data;
      let tipos: string[] = [];

      if (Array.isArray(raw)) {
        tipos = raw.map((x: any) => {
          if (typeof x === 'string') return String(x).trim();
          return String(x?.codigo ?? x?.nome ?? x?.descricao ?? x).trim();
        }).filter(Boolean);
      }

      if (tipos.length > 0) {
        return {
          success: true,
          message: 'Tipos de movimento carregados',
          data: tipos,
          timestamp: new Date().toISOString(),
        };
      }

      // Fallback para lista estática
      return {
        success: true,
        message: 'Tipos de movimento (fallback estático) carregados',
        data: staticTypes,
        timestamp: new Date().toISOString(),
      };
    } catch (e) {
      return {
        success: true,
        message: 'Tipos de movimento (erro na API, usando estático) carregados',
        data: staticTypes,
        timestamp: new Date().toISOString(),
      };
    }
  },

  async relatorioMovimentos(params: { dataInicio?: string; dataFim?: string; dataInicial?: string; dataFinal?: string; codigoProduto?: string; vendedorId?: string; tipo?: string; tipoMovimentacao?: string }): Promise<ApiResponse<any>> {
    const sp = new URLSearchParams();
    const di = params?.dataInicio ?? params?.dataInicial;
    const df = params?.dataFim ?? params?.dataFinal;
    if (di) sp.append('dataInicio', di);
    if (df) sp.append('dataFim', df);
    if (params?.codigoProduto) sp.append('codigoProduto', params.codigoProduto);
    if (params?.vendedorId) sp.append('vendedorId', params.vendedorId);
    const tipo = params?.tipo ?? params?.tipoMovimentacao;
    if (tipo) sp.append('tipo', tipo);
    const qs = sp.toString();
    const url = qs ? `${movementsBaseUrl}/relatorio?${qs}` : `${movementsBaseUrl}/relatorio`;
    const response = await api.get<ApiResponse<any>>(url);
    return response.data;
  },
};

export default stockService;